import { createSlice } from '@reduxjs/toolkit';
import { REQUEST_STATUS } from '../constants';
import {
  fetchPopulationMetrics,
  fetchBaselineFollowupMetrics,
  fetchSurveysData,
  exportPopulationData,
  fetchAlertPatientDetails,
  fetchBaselineFollowupPatientDetails,
} from '../api';

const initialState = {
  // Main data
  metrics: [],
  totalPatients: null,
  totalBaselinePatients: null,
  totalFollowupPatients: null,
  status: REQUEST_STATUS.idle,
  error: null,

  // Surveys/Assessment types
  assessmentInstruments: [
    { id: 'all', name: 'All Assessments' },
  ],
  surveysStatus: REQUEST_STATUS.idle,
  surveysError: null,

  // Filter settings
  dateRange: {
    startDate: null,
    endDate: null,
  },
  selectedAccount: null,
  selectedInstrument: 'all',
  isComparisonMode: false,

  // Export state
  exportStatus: REQUEST_STATUS.idle,
  exportError: null,

  // UI state
  selectedMetric: null,

  // Patient details state
  patientDetails: {
    alertTitle: null,
    alertType: null,
    patients: [],
    baselinePatientsList: [],
    followupPatientsList: [],
    totalDetailPatients: 0,
    populationPercentage: 0,
    populationDistribution: null,
    status: REQUEST_STATUS.idle,
    error: null,
  },
};

export const populationHealthSlice = createSlice({
  name: 'populationHealth',
  initialState,
  reducers: {
    canselAllFilters: (state, action) => {
      state.metrics = initialState.metrics;
      state.totalPatients = initialState.totalPatients;
      state.totalAlerts = initialState.totalAlerts;
      state.totalBaselinePatients = initialState.totalBaselinePatients;
      state.totalBaselineAlerts = initialState.totalBaselineAlerts;
      state.totalFollowupPatients = initialState.totalFollowupPatients;
      state.totalFollowupAlerts = initialState.totalFollowupAlerts;
      state.status = initialState.status;
      state.error = initialState.error;
      state.assessmentInstruments = initialState.assessmentInstruments;
      state.surveysStatus = initialState.surveysStatus;
      state.surveysError = initialState.surveysError;
      state.selectedAccount = initialState.selectedAccount;
      state.selectedInstrument = initialState.selectedInstrument;
      state.isComparisonMode = initialState.isComparisonMode;
      state.exportStatus = initialState.exportStatus;
      state.exportError = initialState.exportError;
      state.dateRange = initialState.dateRange;
      state.patientDetails = initialState.patientDetails;
      state.selectedMetric = initialState.selectedMetric;
    },
    setDateRange: (state, action) => {
      state.dateRange = action.payload;
    },
    setPopulationHealthAccount: (state, action) => {
      state.selectedAccount = action.payload;
    },
    setAssessmentInstrument: (state, action) => {
      state.selectedInstrument = action.payload;
    },
    setComparisonMode: (state, action) => {
      state.isComparisonMode = action.payload;
    },
    setSelectedMetric: (state, action) => {
      state.selectedMetric = action.payload;
    },
    clearError: (state) => {
      state.error = null;
      state.exportError = null;
    },
    clearPatientDetails: (state) => {
      state.patientDetails = {
        alertTitle: null,
        alertType: null,
        patients: [],
        baselinePatientsList: [],
        followupPatientsList: [],
        totalDetailPatients: 0,
        populationPercentage: 0,
        populationDistribution: null,
        status: REQUEST_STATUS.idle,
        error: null,
      };
    },
  },
  extraReducers(builder) {
    builder
      // Fetch population metrics
      .addCase(fetchPopulationMetrics.pending, (state) => {
        state.status = REQUEST_STATUS.loading;
        state.error = null;
      })
      .addCase(fetchPopulationMetrics.fulfilled, (state, action) => {
        if (action.payload.ok) {
          state.status = REQUEST_STATUS.succeeded;
          state.metrics = action.payload.data.metrics.map(e => ({...e,  percent: Math.round(e.percent)}));
          state.totalPatients = action.payload.data.totalPatients;
          state.totalAlerts = action.payload.data.totalAlerts;
          state.error = null;
        } else {
          state.status = REQUEST_STATUS.failed;
          state.error = action.payload.error?.message || 'Failed to load metrics';
        }
      })
      .addCase(fetchPopulationMetrics.rejected, (state, action) => {
        state.status = REQUEST_STATUS.failed;
        state.error = action.error.message || 'Failed to load metrics';
      })

      // Fetch baseline-followup metrics
      .addCase(fetchBaselineFollowupMetrics.pending, (state) => {
        state.status = REQUEST_STATUS.loading;
        state.error = null;
      })
      .addCase(fetchBaselineFollowupMetrics.fulfilled, (state, action) => {
        if (action.payload.ok) {
          state.status = REQUEST_STATUS.succeeded;
          state.metrics = action.payload.data.metrics.map(e=>({
            ...e,
            baselinePercent: Math.round(e.baselinePercent),
            followupPercent: Math.round(e.followupPercent),
            percent: Math.round(e.percent),
          }));
          state.totalPatients = action.payload.data.totalPatients;
          state.totalAlerts = action.payload.data.totalAlerts;
          state.totalBaselinePatients = action.payload.data.totalBaselinePatients;
          state.totalFollowupPatients = action.payload.data.totalFollowupPatients;
          state.totalBaselineAlerts = action.payload.data.totalBaselineAlerts;
          state.totalFollowupAlerts = action.payload.data.totalFollowupAlerts;
          state.error = null;
        } else {
          state.status = REQUEST_STATUS.failed;
          state.error = action.payload.error?.message || 'Failed to load baseline-followup metrics';
        }
      })
      .addCase(fetchBaselineFollowupMetrics.rejected, (state, action) => {
        state.status = REQUEST_STATUS.failed;
        state.error = action.error.message || 'Failed to load baseline-followup metrics';
      })

      // Export population data
      .addCase(exportPopulationData.pending, (state) => {
        state.exportStatus = REQUEST_STATUS.loading;
        state.exportError = null;
      })
      .addCase(exportPopulationData.fulfilled, (state, action) => {
        // Export successful - action.payload contains csvContent and filename
        state.exportStatus = REQUEST_STATUS.succeeded;
        state.exportError = null;
      })
      .addCase(exportPopulationData.rejected, (state, action) => {
        state.exportStatus = REQUEST_STATUS.failed;
        state.exportError = action.error.message || 'Failed to export data';
      })

      // Fetch surveys data
      .addCase(fetchSurveysData.pending, (state) => {
        state.surveysStatus = REQUEST_STATUS.loading;
        state.surveysError = null;
      })
      .addCase(fetchSurveysData.fulfilled, (state, action) => {
        if (action.payload.ok) {
          state.surveysStatus = REQUEST_STATUS.succeeded;
          state.assessmentInstruments = action.payload.data.instruments;
          state.surveysError = null;
        } else {
          state.surveysStatus = REQUEST_STATUS.failed;
          state.surveysError = action.payload.error?.message || 'Failed to load survey types';
        }
      })
      .addCase(fetchSurveysData.rejected, (state, action) => {
        state.surveysStatus = REQUEST_STATUS.failed;
        state.surveysError = action.error.message || 'Failed to load survey types';
        // Set default instruments as fallback - only "All" option
        state.assessmentInstruments = [
          { id: 'all', name: 'All Assessments' },
        ];
      })

      // Fetch alert patient details
      .addCase(fetchAlertPatientDetails.pending, (state) => {
        state.patientDetails.status = REQUEST_STATUS.loading;
        state.patientDetails.error = null;
      })
      .addCase(fetchAlertPatientDetails.fulfilled, (state, action) => {
        if (action.payload.ok) {
          state.patientDetails.status = REQUEST_STATUS.succeeded;
          state.patientDetails.alertTitle = action.payload.data.alertTitle;
          state.patientDetails.alertType = action.payload.data.alertType;
          state.patientDetails.patients = action.payload.data.patients;
          state.patientDetails.totalDetailPatients = action.payload.data.totalPatients;
          state.patientDetails.populationPercentage = action.payload.data.populationPercentage;
          state.patientDetails.populationDistribution = action.payload.data.populationDistribution;
          state.patientDetails.error = null;
          // Clear baseline/followup lists for non-comparison mode
          state.patientDetails.baselinePatientsList = [];
          state.patientDetails.followupPatientsList = [];
        } else {
          state.patientDetails.status = REQUEST_STATUS.failed;
          state.patientDetails.error = action.payload.error?.message || 'Failed to load patient details';
        }
      })
      .addCase(fetchAlertPatientDetails.rejected, (state, action) => {
        state.patientDetails.status = REQUEST_STATUS.failed;
        state.patientDetails.error = action.error.message || 'Failed to load patient details';
      })

      // Fetch baseline-followup patient details
      .addCase(fetchBaselineFollowupPatientDetails.pending, (state) => {
        state.patientDetails.status = REQUEST_STATUS.loading;
        state.patientDetails.error = null;
      })
      .addCase(fetchBaselineFollowupPatientDetails.fulfilled, (state, action) => {
        if (action.payload.ok) {
          state.patientDetails.status = REQUEST_STATUS.succeeded;
          state.patientDetails.alertTitle = action.payload.data.alertTitle;
          state.patientDetails.alertType = action.payload.data.alertType;
          state.patientDetails.patients = action.payload.data.patients;
          state.patientDetails.baselinePatientsList = action.payload.data.baselinePatientsList;
          state.patientDetails.followupPatientsList = action.payload.data.followupPatientsList;
          state.patientDetails.totalDetailPatients = action.payload.data.baselinePatients + action.payload.data.followupPatients;
          state.patientDetails.error = null;
        } else {
          state.patientDetails.status = REQUEST_STATUS.failed;
          state.patientDetails.error = action.payload.error?.message || 'Failed to load baseline-followup patient details';
        }
      })
      .addCase(fetchBaselineFollowupPatientDetails.rejected, (state, action) => {
        state.patientDetails.status = REQUEST_STATUS.failed;
        state.patientDetails.error = action.error.message || 'Failed to load baseline-followup patient details';
      });
  },
});

export const {
  setDateRange,
  setPopulationHealthAccount,
  setAssessmentInstrument,
  setComparisonMode,
  setSelectedMetric,
  clearError,
  clearPatientDetails,
  canselAllFilters,
} = populationHealthSlice.actions;

export default populationHealthSlice.reducer;
